import { publicOrigin } from '@maxpreps/network/src/origins';
import Router, { NextRouter } from 'next/router';
import { UrlObject } from 'url';
import getNextConfig from './../../constants/getNextConfig';

//const basePath = process.env.ROUTING_PREFIX || '';

// copied from \maxpreps-fe-react\node_modules\next\dist\shared\lib\router\router.d.ts
interface TransitionOptions {
  shallow?: boolean;
  locale?: string | false;
  scroll?: boolean;
}
type Url = UrlObject | string;

const { appEnv, subdomain, basePath } = getNextConfig().publicRuntimeConfig;

// This is also defined in the getReactPage.js file
export const projectsThatNeedToHaveBasePathInRewrittenPath = [
  '/photography',
  '/video'
  // 'concordia' // technically the sports page does but we don't need to unrewrite that
];

export const cleanAsAlias = (asAlias: Url): Url => {
  // nextRoute can be an object too
  // if it is, don't try to clean it. No use case for this, at least not yet
  if (!asAlias) {
    return asAlias;
  }

  if (typeof asAlias === 'string') {
    try {
      const prefixBasePath =
        basePath &&
        asAlias.startsWith('/') &&
        !asAlias.startsWith(basePath) &&
        projectsThatNeedToHaveBasePathInRewrittenPath.includes(basePath);

      return cleanAsAlias(
        new URL(
          (prefixBasePath ? basePath : '') + asAlias,
          typeof window !== 'undefined' ? window.location.origin : publicOrigin
        )
      );
    } catch (error) {
      // do nothing...
      //console.log('Error', error);
    }

    return asAlias;
  }

  // since asAlias could be relative
  // we need to ensure we only change the host
  // if it's fully quallified
  if (asAlias.host) {
    // if we are working on local,
    // swap out the origin for local
    if (appEnv === 'dev') {
      asAlias.host = 'z.maxpreps.com:3000';
    }
    // if we are working on a hosted 'brach',
    // swap out the origin for that hosted branch
    else if (
      appEnv === 'development' &&
      (subdomain?.indexOf('branch') ?? -1) > -1 &&
      !(asAlias.pathname?.endsWith('.aspx') ?? false) &&
      asAlias.host === 'dev.maxpreps.com'
    ) {
      asAlias.host = `${subdomain}.fe.maxpreps.com`;
    }
  }

  return asAlias;
};

const HOST_PROTOCOL_REGEX = /https:\/\/.*\.maxpreps.com(:3000)?/i;

export const cleanNextRoute = (nextRoute: Url) => {
  // nextRoute can be an object too
  // if it is, don't try to clean it. No use case for this, at least not yet
  if (!nextRoute || typeof nextRoute !== 'string') {
    return nextRoute;
  }

  return nextRoute.replace(HOST_PROTOCOL_REGEX, '');
};

// the 'push' wasn't updating the url address bar.
// by default, if not provided, it should just use the nextRoute that was provided.
export const replace = (
  nextRoute: Url,
  aliasAs: Url = nextRoute,
  options: TransitionOptions | undefined = undefined
) => Router.replace(cleanNextRoute(nextRoute), cleanAsAlias(aliasAs), options);

export const push = (
  nextRoute: Url,
  aliasAs: Url = nextRoute,
  options: TransitionOptions | undefined = undefined
) => Router.push(cleanNextRoute(nextRoute), cleanAsAlias(aliasAs), options);

export const getPageKey = (router: NextRouter) =>
  router.asPath.split('/').pop()?.split('?')[0];

// Add or replace query param without reloading the page
export const setQueryParam = (param: string, value: string) => {
  if (!window?.location) return;
  const { origin, pathname, search } = window.location;

  var searchParams = new URLSearchParams(search);
  searchParams.set(param, value);

  const newurl = `${origin}${pathname}?${searchParams.toString()}`;

  history.replaceState({ path: newurl }, '', newurl);
};
